import React from "react";
import {
  Sparkles, Heart, Activity, Shield, Leaf, MessageCircleHeart,
  RotateCcw, Globe, Aperture, ScrollText, ImageOff,
} from "lucide-react";

const TIER_IMG = {
  Hund: "/dog_full.png",
  Katze: "/cat_full.png",
  Pferd: "/horse_full.png",
};

const zustandColor = (z = "") => {
  const v = z.toLowerCase();
  if (v.includes("offen")) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (v.includes("blockiert")) return "bg-rose-50 text-rose-700 border-rose-200";
  if (v.includes("überaktiv")) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-sky-50 text-sky-700 border-sky-200";
};

const SectionHead = ({ icon: Icon, kicker, children, sub }) => (
  <div className="text-center mb-12">
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full gold-frame bg-white/60 mb-4">
      <Icon className="text-gold" size={26} />
    </div>
    {kicker && <p className="font-script text-3xl text-gold leading-none mb-1">{kicker}</p>}
    <h2 className="font-display text-3xl sm:text-4xl font-semibold text-slate-800">{children}</h2>
    {sub && <p className="text-slate-500 mt-2 max-w-xl mx-auto text-base">{sub}</p>}
    <span className="divider-gold w-32 inline-block mt-6" />
  </div>
);

const Card = ({ children, className = "", testid }) => (
  <div data-testid={testid} className={`card-elegant rounded-2xl p-6 sm:p-8 fade-up ${className}`}>
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div className="py-2.5 border-b border-amber-100/70 last:border-0">
    <p className="font-play text-[13px] uppercase tracking-[0.12em] text-gold mb-1">{label}</p>
    <p className="text-slate-700 leading-loose text-[15px] sm:text-base">{value}</p>
  </div>
);

export const ResultReport = ({ data, onReset }) => {
  const tier = data.tier || {};
  const aura = data.aura || {};
  const chakren = data.chakren || [];
  const blockaden = data.blockaden || [];
  const gesundheit = data.gesundheit || [];
  const lenormand = data.lenormand || [];
  const verhalten = data.verhalten || {};
  const abschluss = data.abschluss || {};
  const tierImg = TIER_IMG[tier.tierart];

  return (
    <div className="relative min-h-screen px-4 py-16 sm:py-24" data-testid="result-report">
      <div className="max-w-5xl mx-auto">
        {/* HEADER */}
        <div className="text-center mb-20 fade-up">
          {tierImg && (
            <img src={tierImg} alt={tier.tierart} className="animal-elegant w-48 sm:w-64 mx-auto mb-2 float-soft" />
          )}
          <p className="font-play italic text-lg text-slate-500">Seelen-Analyse für</p>
          <h1 className="title-script text-6xl sm:text-8xl my-1">{tier.name}</h1>
          <p className="text-slate-500 text-base tracking-wide">
            {tier.tierart} · {tier.geschlecht} · {tier.alter}
            {tier.geburtsdatum ? ` · ${tier.geburtsdatum}` : ""}
          </p>
        </div>

        {/* AURA */}
        <SectionHead icon={Sparkles} kicker="Aura" sub="Die energetische Ausstrahlung und ihre Farben.">
          Aura-Analyse
        </SectionHead>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-28">
          <Card testid="aura-image-card" className="flex flex-col items-center justify-center">
            {data.aura_image ? (
              <img src={data.aura_image} alt={`Aura von ${tier.name}`} data-testid="aura-image"
                className="rounded-xl w-full object-cover shadow-lg gold-frame" />
            ) : data.aura_image_failed ? (
              <div className="w-full aspect-square rounded-xl bg-gradient-to-br from-amber-50 to-violet-50 flex flex-col items-center justify-center gap-3 text-center px-6">
                <ImageOff className="text-slate-300" size={40} />
                <p className="text-sm text-slate-500">Das Aura-Bild konnte gerade nicht gemalt werden – die Deutung unten ist dennoch vollständig.</p>
              </div>
            ) : (
              <div className="w-full aspect-square rounded-xl bg-gradient-to-br from-amber-50 to-violet-50 flex flex-col items-center justify-center gap-3">
                <Sparkles className="text-gold spin-slow" size={42} />
                <p className="text-sm text-slate-500 animate-pulse">Das Aura-Bild wird gemalt…</p>
              </div>
            )}
            <div className="flex items-center gap-3 mt-5">
              <span className="w-7 h-7 rounded-full border-2 border-white shadow" style={{ background: aura.primaerfarbe_hex || "#c8a04a" }} />
              <span className="w-7 h-7 rounded-full border-2 border-white shadow" style={{ background: aura.sekundaerfarbe_hex || "#c084fc" }} />
              <span className="text-[15px] text-slate-600 font-play italic">{aura.primaerfarbe} &amp; {aura.sekundaerfarbe}</span>
            </div>
          </Card>
          <Card testid="aura-text-card">
            <Row label="Primärfarbe" value={aura.primaerfarbe} />
            <Row label="Sekundärfarbe" value={aura.sekundaerfarbe} />
            <Row label="Farbübergänge" value={aura.farbuebergaenge} />
            <Row label="Intensität" value={aura.intensitaet} />
            <Row label="Bedeutung für den Körper" value={aura.bedeutung_koerper} />
            <Row label="Bedeutung für die Emotion" value={aura.bedeutung_emotion} />
            <Row label="Bedeutung für das Verhalten" value={aura.bedeutung_verhalten} />
          </Card>
        </div>

        {/* CHAKREN */}
        <SectionHead icon={Aperture} kicker="Chakren" sub="Die Energiezentren deines Tieres und ihre Heilimpulse.">
          Chakren-Analyse
        </SectionHead>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-28">
          {chakren.map((c, i) => (
            <Card key={i} testid={`chakra-card-${i}`}>
              <div className="flex items-center justify-between mb-3 gap-2">
                <h3 className="font-display text-2xl font-semibold text-slate-800">{c.name}</h3>
                <span className={`text-xs font-medium px-3 py-1 rounded-full border whitespace-nowrap ${zustandColor(c.zustand)}`}>{c.zustand}</span>
              </div>
              <Row label="Bedeutung" value={c.bedeutung} />
              <Row label="Auswirkung auf Verhalten" value={c.auswirkung_verhalten} />
              <Row label="Auswirkung auf Gesundheit" value={c.auswirkung_gesundheit} />
              <Row label="Heilimpuls" value={c.heilimpuls} />
            </Card>
          ))}
        </div>

        {/* BLOCKADEN */}
        <SectionHead icon={Shield} kicker="Blockaden" sub="Erkannte energetische Blockaden und Lösungswege.">
          Blockaden &amp; energetische Themen
        </SectionHead>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-28">
          {blockaden.map((b, i) => (
            <Card key={i} testid={`blockade-card-${i}`}>
              <h3 className="font-display text-2xl font-semibold text-rose-700/90 mb-3">{b.titel}</h3>
              <Row label="Beschreibung" value={b.beschreibung} />
              <Row label="Ursache" value={b.ursache} />
              <Row label="Auswirkung" value={b.auswirkung} />
              <Row label="Lösungsschritt" value={b.loesungsschritt} />
            </Card>
          ))}
        </div>

        {/* GESUNDHEIT */}
        <SectionHead icon={Activity} kicker="Gesundheit" sub="Rein energetische Hinweise – keine medizinischen Diagnosen.">
          Gesundheitsenergie
        </SectionHead>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-28">
          {gesundheit.map((g, i) => (
            <Card key={i} testid={`gesundheit-card-${i}`} className="!p-6">
              <div className="flex items-center gap-2 mb-2">
                <Leaf className="text-emerald-500" size={20} />
                <h3 className="font-display font-semibold text-slate-800 text-xl">{g.bereich}</h3>
              </div>
              <p className="text-slate-700 leading-loose text-[15px]">{g.hinweis}</p>
            </Card>
          ))}
        </div>

        {/* LENORMAND */}
        <SectionHead icon={ScrollText} kicker="Lenormand" sub="Eine 5-Karten-Legung speziell für die Tierseele.">
          Lenormand-Tierlegung
        </SectionHead>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-28">
          {lenormand.map((k, i) => (
            <Card key={i} testid={`lenormand-card-${i}`}
              className={i === lenormand.length - 1 && lenormand.length % 2 !== 0 ? "md:col-span-2" : ""}>
              <div className="flex items-center justify-between mb-4 gap-2 pb-3 border-b border-amber-100">
                <span className="font-play text-xs uppercase tracking-wider px-3 py-1 rounded-full bg-violet-50 text-violet-700 border border-violet-100">
                  {i + 1}. {k.position}
                </span>
                <span className="font-script text-3xl sm:text-4xl text-gold leading-none">{k.karte}</span>
              </div>
              <Row label="Klassische Bedeutung" value={k.klassische_bedeutung} />
              <Row label="Bedeutung für Tiere" value={k.bedeutung_tier} />
              <Row label="Emotionale Bedeutung" value={k.emotionale_bedeutung} />
              <Row label="Körperliche Bedeutung" value={k.koerperliche_bedeutung} />
              <Row label="Verhaltensthema" value={k.verhaltensthema} />
              <Row label="Heilimpuls" value={k.heilimpuls} />
            </Card>
          ))}
        </div>

        {/* VERHALTEN */}
        <SectionHead icon={Heart} kicker="Bedürfnisse" sub="Was dein Tier gerade fühlt und braucht.">
          Verhalten &amp; Bedürfnisse
        </SectionHead>
        <Card testid="verhalten-card" className="mb-28">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12">
            <Row label="Was dein Tier braucht" value={verhalten.braucht} />
            <Row label="Was es belastet" value={verhalten.belastet} />
            <Row label="Was es stärkt" value={verhalten.staerkt} />
            <Row label="Welche Umgebung gut ist" value={verhalten.umgebung} />
            <Row label="Welche Menschenenergie es mag" value={verhalten.menschenenergie} />
            <Row label="Bindungsenergie zum Besitzer" value={verhalten.bindungsenergie} />
          </div>
        </Card>

        {/* GESAMTDEUTUNG */}
        <SectionHead icon={ScrollText} kicker="Gesamtdeutung" sub="Alle Ebenen der Analyse zu einem Gesamtbild vereint.">
          Zusammenstellung der Deutung
        </SectionHead>
        <div data-testid="gesamtdeutung-card" className="card-elegant rounded-[2rem] p-8 sm:p-14 mb-14 fade-up">
          <p className="first-drop font-play text-xl sm:text-2xl text-slate-700 leading-loose">
            {abschluss.gesamtdeutung || abschluss.zusammenfassung}
          </p>
          <span className="divider-gold w-full my-9 block" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12">
            <Row label="Zusammenfassung" value={abschluss.zusammenfassung} />
            <Row label="Heilimpulse" value={abschluss.heilimpulse} />
            <Row label="Empfehlung" value={abschluss.empfehlung} />
          </div>
        </div>

        {/* SEELENBOTSCHAFT */}
        <div data-testid="seelenbotschaft-card"
          className="card-elegant rounded-[2rem] p-10 sm:p-16 text-center mb-16 fade-up">
          <Heart className="text-rose-300 mx-auto mb-5 float-soft" size={34} />
          <p className="font-script text-4xl sm:text-5xl text-gold mb-6">Seelenbotschaft</p>
          <p className="font-play italic text-2xl sm:text-3xl text-slate-700 leading-relaxed max-w-2xl mx-auto">
            „{abschluss.seelenbotschaft}"
          </p>
          <p className="mt-6 text-slate-400 text-sm">— {tier.name}</p>
        </div>

        {/* WEBSITE HINT */}
        <div data-testid="website-hint"
          className="card-elegant rounded-2xl p-6 flex items-start gap-4 mb-12 max-w-3xl mx-auto">
          <Globe className="text-gold shrink-0 mt-1" size={22} />
          <p className="text-slate-600 leading-loose text-[15px]">
            Wenn die App vollständig eingerichtet ist, stelle bitte den Link zur Webseite oder
            App-Ansicht öffentlich bereit, damit alle Nutzer direkt darauf zugreifen können.
          </p>
        </div>

        <div className="text-center">
          <button data-testid="reset-btn" onClick={onReset}
            className="gold-btn rounded-full px-10 py-4 font-play text-base uppercase inline-flex items-center gap-2">
            <RotateCcw size={18} /> Neue Analyse
          </button>
        </div>
      </div>
    </div>
  );
};
